import {WorkspacesDataSource} from "./WorkspacesDataSource";
import {ItemsDataSource} from "./ItemsDataSource";
import {QuizzesDataSource} from "./QuizzesDataSource";
import {TaskListUnitDS} from "../taskLists/TaskListUnitDS";
import {ClientActiveRecord} from "./ClientActiveRecord";
import {WsUsersDataSource} from "./WsUsersDataSource";
import {UsersDataSource} from "./UsersDataSource";
import {UserWorkspacesDataSource} from "./UserWorkspacesDataSource";
import {NotificationsDataSource} from "./NotificationsDataSource";

import {Workspace} from "../../API";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import {AppState} from "../AppState";


type WorkspaceId = string;


export class DataSourceManager {
    public dataClientService: IDataClientService;
    private authService: IAuthService;
    private appState: AppState;

    workspacesDS: WorkspacesDataSource;
    notificationsDS: NotificationsDataSource;
    userWorkspacesDS: UserWorkspacesDataSource;
    usersDS: UsersDataSource;
    clientAR: ClientActiveRecord;

    itemsByWs: Map<WorkspaceId, ItemsDataSource> = new Map();
    quizzesByWs: Map<WorkspaceId, QuizzesDataSource> = new Map();
    usersByWs: Map<WorkspaceId, WsUsersDataSource> = new Map();
    taskListUnitsByWs: Map<WorkspaceId, TaskListUnitDS> = new Map();

    constructor(
        dataClientService: IDataClientService,
        authService: IAuthService,
        appState: AppState,
        tenantId: string,
        currentUserId: string
    ) {
        this.appState = appState;
        this.dataClientService = dataClientService;
        this.authService = authService;

        // Create DataSources with proper dependencies
        this.workspacesDS = new WorkspacesDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
            rootStore: this.appState.rootStore,
        });

        this.notificationsDS = new NotificationsDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        });

        this.userWorkspacesDS = new UserWorkspacesDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        }, currentUserId);

        this.usersDS = new UsersDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        });

        this.clientAR = new ClientActiveRecord({
                dataClientService,
                authService,
            },
            tenantId
        );
    }

    async init() {
        await this.workspacesDS.initialLoad();
        await this.notificationsDS.initialLoad(false);
        await this.clientAR.initialLoad();
        await this.userWorkspacesDS.initialLoad();
    }

    getWorkspaceById(wsId: string): Workspace | undefined {
        return this.workspacesDS.allRows.find(wsDB => wsDB.id === wsId);
    }

    destroy() {
        this.workspacesDS.destroy();
        this.notificationsDS.destroy();
        this.userWorkspacesDS.destroy();
        this.usersDS.destroy();
        this.clientAR.destroy();

        Array.from(this.itemsByWs.values()).map(dataSource => dataSource.destroy());
        Array.from(this.quizzesByWs.values()).map(dataSource => dataSource.destroy());
        Array.from(this.taskListUnitsByWs.values()).map(dataSource => dataSource.destroy());
    }

    getItemsDataSource(wsId: WorkspaceId) {
        if (!this.itemsByWs.has(wsId)) {
            this.itemsByWs.set(wsId, new ItemsDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                rootStore: this.appState.rootStore,
            }, wsId));
        }
        return this.itemsByWs.get(wsId)!;
    }

    getQuizzesDataSource(wsId: WorkspaceId) {
        if (!this.quizzesByWs.has(wsId)) {
            this.quizzesByWs.set(wsId, new QuizzesDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                appState: this.appState
            }, wsId));
        }
        return this.quizzesByWs.get(wsId)!;
    }

    getWsUsersDataSource(wsId: WorkspaceId) {
        if (!this.usersByWs.has(wsId)) {
            this.usersByWs.set(wsId, new WsUsersDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                rootStore: this.appState.rootStore,
            }, wsId));
        }
        return this.usersByWs.get(wsId)!;
    }

    getTaskListUnitsDataSource(wsId: WorkspaceId) {
        if (!this.taskListUnitsByWs.has(wsId)) {
            this.taskListUnitsByWs.set(wsId, new TaskListUnitDS({
                dataClientService: this.dataClientService,
                authService: this.authService
            }, wsId));
        }
        return this.taskListUnitsByWs.get(wsId)!;
    }

    getDataClientService(): IDataClientService {
        return this.dataClientService;
    }
}
